import React, { type FC } from 'react'
import { useState } from 'react';
import { ChevronDownIcon, DotsIcon, EditIcon, MergeIcon, TableGridIcon, TrashIcon } from '../Icons';
import DropdownMenu from './DropdownMenu';
import type { SectionItem, TableGroup, TableItem } from '../reservations.types';
import { Plus } from 'lucide-react';

// ─── STATUS BADGE ────────────────────────────────────────────────────────────
const StatusBadge: FC<{ active: boolean }> = ({ active }) => (
  <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full
    ${active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
    <div className={`w-1.5 h-1.5 rounded-full ${active ? "bg-green-500" : "bg-gray-300"}`} />
    {active ? "Active" : "Inactive"}
  </span>
);

const TableCard: FC<{
  table: TableItem;
  groups: TableGroup[];
  allTables: TableItem[];
  onEdit: () => void;
  onDelete: () => void;
}> = ({ table, groups, allTables, onEdit, onDelete }) => {
  const mergedGroup = groups.find(g => g.isActive && g.tableIds.includes(table.id));
  const combinableNames = (table.combinableWith ?? [])
    .map(id => allTables.find(t => t.id === id)?.name)
    .filter(Boolean);

  return (
    <div className={`relative bg-white border-2 rounded-2xl px-5 py-4 transition-all group
      ${!table.isActive ? "border-gray-100 opacity-60" : mergedGroup ? "border-amber-300 bg-amber-50/50" : "border-gray-200 hover:border-orange-300 hover:shadow-md"}`}>

      {/* Merged badge */}
      {mergedGroup && (
        <div className="absolute -top-2.5 left-4">
          <span className="bg-amber-400 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">MERGED</span>
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="m-0 text-xl font-extrabold text-stone-900 leading-none">{table.name}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-gray-500 font-medium">{table.capacity} seats</span>
            <span className="text-gray-200">·</span>
            <StatusBadge active={table.isActive} />
          </div>
        </div>

        <DropdownMenu
          trigger={
            <button className="p-1.5 rounded-lg text-gray-400 hover:text-stone-700 hover:bg-gray-100 border-none bg-transparent cursor-pointer transition-colors opacity-0 group-hover:opacity-100">
              <DotsIcon />
            </button>
          }
          items={[
            { label: "Edit Table", icon: <EditIcon />, onClick: onEdit },
            { label: "Archive",    icon: <TrashIcon />, onClick: onDelete, danger: true },
          ]}
        />
      </div>

      {table.notes && (
        <p className="m-0 text-xs text-gray-400 leading-relaxed mb-2 truncate" title={table.notes}>
          {table.notes}
        </p>
      )}

      {combinableNames.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {combinableNames.map((n, i) => (
            <span key={i} className="text-[10px] bg-blue-50 text-blue-500 px-2 py-0.5 rounded-md font-medium">⇄ {n}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionCard: FC<{
  section: SectionItem;
  groups: TableGroup[];
  allTables: TableItem[];
  onEditSection: () => void;
  onDeleteSection: () => void;
  onAddTable: () => void;
  onEditTable: (t: TableItem) => void;
  onDeleteTable: (t: TableItem) => void;
  onMerge: () => void;
}> = ({ section, groups, allTables, onEditSection, onDeleteSection, onAddTable, onEditTable, onDeleteTable, onMerge }) => {
  const [collapsed, setCollapsed] = useState(false);
  const activeCount = section.tables.filter(t => t.isActive).length;

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden shadow-sm transition-all ${section.isActive ? "border-gray-100" : "border-gray-100 opacity-70"}`}>
      {/* Section header */}
      <div className='flex flex-col px-3 md:px-6 py-4 bg-gray-50 border-b border-gray-100'>
        <span className="text-base font-bold text-stone-900">{section.name}</span>
        <div className="flex items-center gap-3">
            <button onClick={() => setCollapsed(c => !c)}
            className="text-gray-400 hover:text-stone-700 bg-transparent border-none cursor-pointer flex items-center p-0 transition-colors">
            <ChevronDownIcon open={!collapsed} />
            </button>

            <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge active={section.isActive} />
                <span className="text-xs text-gray-400">{activeCount}/{section.tables.length} tables active</span>
            </div>
            {section.description && (
                <p className="m-0 mt-0.5 text-xs text-gray-400 truncate">{section.description}</p>
            )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onMerge}
                className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border-none rounded-xl px-3 py-1.5 text-xs font-semibold cursor-pointer transition-colors">
                <MergeIcon /> Merge
            </button>
            <button onClick={onAddTable}
                className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-3 py-1.5 text-xs font-bold cursor-pointer transition-colors">
                <Plus size={16}  /> Table
            </button>
            <DropdownMenu
                trigger={
                <button className="p-2 rounded-xl text-gray-400 hover:text-stone-700 hover:bg-gray-200 border border-gray-200 bg-white cursor-pointer transition-colors flex items-center">
                    <DotsIcon />
                </button>
                }
                items={[
                { label: "Edit Section",   icon: <EditIcon />,  onClick: onEditSection },
                { label: "Archive Section",icon: <TrashIcon />, onClick: onDeleteSection, danger: true },
                ]}
            />
            </div>
        </div>
      </div>

      {/* Tables grid */}
      {!collapsed && (
        <div className="p-3 md:p-6">
          {section.tables.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-300">
              <TableGridIcon />
              <p className="m-0 mt-3 text-sm">No tables yet.</p>
              <button onClick={onAddTable}
                className="mt-3 text-orange-500 hover:text-orange-600 bg-transparent border-none cursor-pointer text-sm font-semibold underline transition-colors">
                Add the first table
              </button>
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
              {section.tables.map(t => (
                <TableCard key={t.id} table={t} groups={groups} allTables={allTables}
                  onEdit={() => onEditTable(t)}
                  onDelete={() => onDeleteTable(t)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default SectionCard